import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { Account, LoggerService } from '../../shared/sdk';
import { AccountService } from '../account.service';
import { OrganizationService } from '../../organizations/organization.service';
import { validEmailValidator } from '../../shared/directives/valid-email.directive';
import { StorageService } from '../../shared/services/storage.service';
import { WhiteLabelService } from '../../shared/services/whitelabel.service';
import { TitleService } from '../../shared/services/title.service';
import { ToastService } from '../../shared/modules/toast/toast.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
})
export class LoginComponent implements OnInit {
  complete: boolean;
  loading: boolean;
  loginForm: FormGroup;
  sent: boolean;
  integrating: any = {};
  private account: any;
  private returnUrl: string;

  constructor(
    public accountService: AccountService,
    public orgService: OrganizationService,
    public translate: TranslateService,
    public WL: WhiteLabelService,
    private fb: FormBuilder,
    private log: LoggerService,
    private route: ActivatedRoute,
    private router: Router,
    private storageService: StorageService,
    private titleService: TitleService,
    private toastService: ToastService,
  ) {}

  ngOnInit() {
    this.titleService.set(['Login']);
    this.loading = false;
    this.sent = false;
    this.account = new Account();
    this.returnUrl = this.route.snapshot.queryParams.returnUrl;

    this.loginForm = this.fb.group({
      email: [
        '',
        Validators.compose([Validators.required, validEmailValidator()]),
      ],
      password: ['', Validators.required],
      rememberMe: [true],
    });

    const storedReturnUrl = this.storageService.getItem('Apollo:returnUrl');

    // Integration flow step (Shopify)
    // User will be redirected here from /organizations/choose
    // if he is not logged in or doesn't have an account.
    // After logging in or signing up for the first time we pick
    // up on the stored value and return to the process.
    // NOTE: If users is signing up for the first time he won't
    // have an organization yet so we must also persist through that.

    // First time visit for integration
    if (!storedReturnUrl) {
      // Save for return
      if (this.returnUrl) {
        // Shopify
        if (this.returnUrl.includes('myshopify')) {
          // Save return
          this.storageService.setItem('Apollo:returnUrl', this.returnUrl);
        }
      }
    } else if (!this.returnUrl) {
      // Returning integration visitor
      this.returnUrl = storedReturnUrl;
    }
  }

  onLogin() {
    this.sent = true;
    const returnUrl = this.returnUrl;

    if (this.loginForm.valid) {
      this.account.email = this.loginForm.value.email;
      this.account.password = this.loginForm.value.password;

      this.loading = true;

      this.accountService
        .login(this.account, undefined, this.loginForm.value.rememberMe)
        // .pipe(switchMap(() => this.orgService.get(undefined, undefined, true)))
        .subscribe(
          () => {
            this.orgService.clearCache();

            // Take precedence over number of organizations in case
            // continuing integration
            if (returnUrl) {
              let url = returnUrl;
              const tokenIdx = url.indexOf('access_token');

              // Strip token from url if passed for login
              if (tokenIdx !== -1) {
                url = url.slice(0, tokenIdx - 1);
              }

              return this.router.navigateByUrl(returnUrl);
            }

            return this.router.navigate(['/organizations/choose']);
          },
          (err) => {
            // E-mail not verified
            this.loading = false;
            this.log.error(err);

            if (err.code === 'LOGIN_FAILED_EMAIL_NOT_VERIFIED') {
              this.toastService.warning(
                this.translate.instant(
                  'Please check your e-mail, we have sent you a verification link.',
                ),
                this.translate.instant('E-mail not verified'),
              );
            } else if (err.statusCode === 401) {
              // Invalid credentials
              this.toastService.warning(
                this.translate.instant('login.toast.error.body'),
                this.translate.instant('login.toast.error.title'),
              );
            } else {
              this.toastService.warning(
                this.translate.instant('error.something-went-wrong.title'),
                this.translate.instant('error.something-went-wrong.message'),
              );
            }
          },
        );
    }
  }
}
